import { useContext, useEffect, useMemo, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { LanguageContext } from '../App';

const FloatingAnnouncement = ({ settings }) => {
  const { t } = useContext(LanguageContext);
  const message = String(settings?.floating_announcement_message || '').trim();
  const enabled = Boolean(settings?.floating_announcement_enabled) && Boolean(message);
  const [dismissed, setDismissed] = useState(false);

  const storageKey = useMemo(() => {
    if (!message) return '';
    return `kayi:floating-announcement:session-dismissed:${message}`;
  }, [message]);

  useEffect(() => {
    if (!storageKey) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(storageKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  const handleDismiss = () => {
    setDismissed(true);
    if (storageKey) {
      try {
        sessionStorage.setItem(storageKey, '1');
      } catch {
        // Ignore storage write errors
      }
    }
  };

  if (!enabled || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[45] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('floatingAnnouncementLabel')}>
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={handleDismiss}
        aria-label={t('floatingAnnouncementDismiss')}
      />

      <div className="floating-announcement relative w-full max-w-md rounded-2xl border border-green-500/25 bg-[#111]/98 backdrop-blur-md shadow-2xl shadow-black/50 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-green-500/40 via-green-400/70 to-green-500/40" />

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0">
              <Bell size={20} className="text-green-400" />
            </div>

            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-green-400 text-xs font-semibold uppercase tracking-wide mb-2">
                {t('floatingAnnouncementLabel')}
              </p>
              <div className="max-h-[42vh] overflow-y-auto pr-1">
                <p className="text-white/90 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
                  {message}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              className="text-white/40 hover:text-white transition p-1 -mr-1 -mt-1 flex-shrink-0"
              aria-label={t('floatingAnnouncementDismiss')}
            >
              <X size={18} />
            </button>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="mt-5 w-full py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
          >
            {t('floatingAnnouncementDismiss')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingAnnouncement;
